import {
  normalizeBoothCode,
  type LabeledBoothRect,
} from "./floorplanSvg.js";

export type AutoPlaceCompanyInput = {
  id: string;
  booth?: string | null;
  mapLocked?: boolean | null;
  /** True when company already has map_x/map_y set. */
  placed?: boolean;
};

export type AutoBoothPlacement = {
  companyId: string;
  booth: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type AutoPlacePlan = {
  placements: AutoBoothPlacement[];
  /** Companies with a booth number but no matching rect on the plan. */
  unmatchedCompanies: Array<{ id: string; booth: string }>;
  /** Labeled booths on the plan with no company. */
  unusedBooths: string[];
  skippedLocked: number;
};

/**
 * Match exhibitors to labeled booth rectangles and emit pin boxes (%).
 * Several companies on one booth are stacked vertically inside the cell.
 */
export function planAutoBoothPlacements(
  companies: AutoPlaceCompanyInput[],
  labeledBooths: LabeledBoothRect[],
  options?: { overwritePlaced?: boolean },
): AutoPlacePlan {
  const overwritePlaced = options?.overwritePlaced !== false;
  const byCode = new Map<string, LabeledBoothRect>();
  for (const b of labeledBooths) {
    const code = normalizeBoothCode(b.code);
    if (!code) continue;
    if (!byCode.has(code)) byCode.set(code, b);
  }

  const groups = new Map<string, AutoPlaceCompanyInput[]>();
  const unmatchedCompanies: Array<{ id: string; booth: string }> = [];
  let skippedLocked = 0;

  for (const c of companies) {
    const booth = normalizeBoothCode(c.booth);
    if (!booth) continue;
    if (c.mapLocked) {
      skippedLocked += 1;
      continue;
    }
    if (c.placed && !overwritePlaced) continue;
    if (!byCode.has(booth)) {
      unmatchedCompanies.push({ id: c.id, booth });
      continue;
    }
    const list = groups.get(booth) ?? [];
    list.push(c);
    groups.set(booth, list);
  }

  const placements: AutoBoothPlacement[] = [];
  const usedCodes = new Set<string>();

  for (const [booth, members] of groups) {
    const box = byCode.get(booth);
    if (!box) continue;
    usedCodes.add(booth);
    const n = members.length;
    const sliceH = box.h / n;
    for (let i = 0; i < n; i++) {
      const c = members[i]!;
      const h = Math.max(1.2, sliceH * 0.92);
      const w = Math.max(1.2, box.w);
      const cy = box.y + sliceH * (i + 0.5);
      const cx = box.x + box.w / 2;
      placements.push({
        companyId: c.id,
        booth,
        x: Number(cx.toFixed(2)),
        y: Number(cy.toFixed(2)),
        w: Number(w.toFixed(2)),
        h: Number(h.toFixed(2)),
      });
    }
  }

  const unusedBooths = [...byCode.keys()].filter((code) => !usedCodes.has(code)).sort();

  return { placements, unmatchedCompanies, unusedBooths, skippedLocked };
}
